/**
 * LERA Delivery Countdown — Sprint 8.3B.1 / 8.3B.1.1
 * Independent runtime. America/Santiago timezone. Updates every second.
 *
 * Countdown dispatches lera:popover:toggle — popover runtime in lera-delivery-popover.js.
 */

const LERA_DC_TIMEZONE = 'America/Santiago';
const LERA_DC_TICK_MS = 1000;

function leraDcGetSantiagoParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LERA_DC_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const hour = Number(parts.find((part) => part.type === 'hour')?.value || 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value || 0);
  const second = Number(parts.find((part) => part.type === 'second')?.value || 0);
  return {
    hour,
    minute,
    second,
    totalMinutes: hour * 60 + minute,
    totalSeconds: hour * 3600 + minute * 60 + second,
  };
}

function leraDcPad(value) {
  return String(value).padStart(2, '0');
}

function leraDcSplitCountdown(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return { hours, minutes, seconds };
}

function leraDcFormatCountdown(totalSeconds) {
  const { hours, minutes, seconds } = leraDcSplitCountdown(totalSeconds);
  return `${leraDcPad(hours)}h ${leraDcPad(minutes)}m ${leraDcPad(seconds)}s`;
}

function leraDcFormatDeliveryTime(hour, minute) {
  return `${hour}:${leraDcPad(minute)}`;
}

if (!customElements.get('lera-delivery-countdown')) {
  customElements.define(
    'lera-delivery-countdown',
    class LeraDeliveryCountdown extends HTMLElement {
      constructor() {
        super();
        this.cutoffHour = 15;
        this.cutoffMinute = 0;
        this.deliveryHour = 22;
        this.deliveryMinute = 0;
        this.tickInterval = undefined;
        this.todayMode = null;
        this.tomorrowMode = null;
        this.hoursEl = null;
        this.minutesEl = null;
        this.secondsEl = null;
        this.lastMode = null;
      }

      connectedCallback() {
        this.readConfig();
        this.todayMode = this.querySelector('[data-lera-dc-mode="today"]');
        this.tomorrowMode = this.querySelector('[data-lera-dc-mode="tomorrow"]');
        this.hoursEl = this.querySelector('[data-lera-dc-hours]');
        this.minutesEl = this.querySelector('[data-lera-dc-minutes]');
        this.secondsEl = this.querySelector('[data-lera-dc-seconds]');
        this.wirePopoverTriggers();
        this.render();
        this.tickInterval = setInterval(() => this.render(), LERA_DC_TICK_MS);
      }

      wirePopoverTriggers() {
        this.querySelectorAll('[data-lera-popover-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const popoverId = trigger.getAttribute('aria-controls') || this.dataset.popoverId;
            if (!popoverId) return;
            document.dispatchEvent(
              new CustomEvent('lera:popover:toggle', {
                bubbles: true,
                detail: { popoverId, trigger },
              })
            );
          });
        });
      }

      disconnectedCallback() {
        clearInterval(this.tickInterval);
      }

      readConfig() {
        this.cutoffHour = Number(this.dataset.cutoffHour) || 15;
        this.cutoffMinute = Number(this.dataset.cutoffMinute) || 0;
        this.deliveryHour = Number(this.dataset.deliveryHour) || 22;
        this.deliveryMinute = Number(this.dataset.deliveryMinute) || 0;
      }

      getCutoffSeconds() {
        return this.cutoffHour * 3600 + this.cutoffMinute * 60;
      }

      updateCountdownUnits(totalSeconds) {
        const { hours, minutes, seconds } = leraDcSplitCountdown(totalSeconds);
        const hoursText = `${leraDcPad(hours)}h`;
        const minutesText = ` ${leraDcPad(minutes)}m`;
        const secondsText = ` ${leraDcPad(seconds)}s`;

        if (this.hoursEl && this.hoursEl.textContent !== hoursText) {
          this.hoursEl.textContent = hoursText;
        }
        if (this.minutesEl && this.minutesEl.textContent !== minutesText) {
          this.minutesEl.textContent = minutesText;
        }
        if (this.secondsEl && this.secondsEl.textContent !== secondsText) {
          this.secondsEl.textContent = secondsText;
        }
      }

      render() {
        const { totalSeconds } = leraDcGetSantiagoParts();
        const cutoffSeconds = this.getCutoffSeconds();
        const isBeforeCutoff = totalSeconds < cutoffSeconds;

        if (this.lastMode !== isBeforeCutoff) {
          if (this.todayMode) {
            this.todayMode.hidden = !isBeforeCutoff;
          }
          if (this.tomorrowMode) {
            this.tomorrowMode.hidden = isBeforeCutoff;
          }
          this.lastMode = isBeforeCutoff;
        }

        if (isBeforeCutoff) {
          this.updateCountdownUnits(cutoffSeconds - totalSeconds);
        }
      }
    }
  );
}

if (typeof window !== 'undefined') {
  window.LeraDeliveryCountdown = {
    getSantiagoParts: leraDcGetSantiagoParts,
    splitCountdown: leraDcSplitCountdown,
    formatCountdown: leraDcFormatCountdown,
    formatDeliveryTime: leraDcFormatDeliveryTime,
  };
}
